import baseApi from "./RootBaseApi";
import {
  ChurnReportsApiRaw,
  ChurnReportsNormalized,
  ChurnFilters,
  ChurnManualCancelItem,
  ChurnExpiredItem,
  ChurnDowngradeItem,
  ChurnUpgradeItem,
} from "@/types/churn-reports.types";

export const ChurnReportsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getChurnReports: builder.query<ChurnReportsNormalized, ChurnFilters | void>(
      {
        query: (params) => ({
          url: "admin/reports/subscriptions/churn",
          method: "GET",
          params,
        }),
        transformResponse: (response: unknown): ChurnReportsNormalized => {
          const raw = (response as ChurnReportsApiRaw)?.data
            ? (response as ChurnReportsApiRaw).data
            : (response as unknown as ChurnReportsApiRaw["data"]);

          // Extract pagination data from downgrades object
          const downgradesObj = (raw as { downgrades?: unknown })
            ?.downgrades as
            | {
                items?: ChurnDowngradeItem[];
                totalItems?: number;
                currentPage?: number;
                pageLimit?: number;
                totalPages?: number;
              }
            | undefined;
          const totalItems = downgradesObj?.totalItems;
          const currentPage = downgradesObj?.currentPage;
          const pageLimit = downgradesObj?.pageLimit;
          const totalPages = downgradesObj?.totalPages;

          const manualCancelsSrc = (raw as { manualCancels?: unknown })
            ?.manualCancels as
            | { items?: ChurnManualCancelItem[] }
            | ChurnManualCancelItem[]
            | undefined;
          const manualCancelsArr: ChurnManualCancelItem[] = Array.isArray(
            (manualCancelsSrc as { items?: ChurnManualCancelItem[] })?.items,
          )
            ? (manualCancelsSrc as { items: ChurnManualCancelItem[] }).items
            : Array.isArray(manualCancelsSrc)
              ? (manualCancelsSrc as ChurnManualCancelItem[])
              : [];
          const manualCancels = manualCancelsArr.map(
            (m: ChurnManualCancelItem) => ({
              subscription_type: m.subscription_type
                ? String(m.subscription_type)
                : undefined,
              period: m.period ?? "",
              period_range: m.period_range ?? undefined,
              reason: m.reason,
              cancellations: m.cancellations,
            }),
          );

          const expiredSrc = (raw as { expired?: unknown })?.expired as
            | { items?: ChurnExpiredItem[] }
            | ChurnExpiredItem[]
            | undefined;
          const expiredArr: ChurnExpiredItem[] = Array.isArray(
            (expiredSrc as { items?: ChurnExpiredItem[] })?.items,
          )
            ? (expiredSrc as { items: ChurnExpiredItem[] }).items
            : Array.isArray(expiredSrc)
              ? (expiredSrc as ChurnExpiredItem[])
              : [];
          const expired = expiredArr.map((e: ChurnExpiredItem) => ({
            subscription_type: String(e.subscription_type ?? ""),
            period: e.period ?? "",
            period_range: e.period_range ?? undefined,
            reason: e.reason ?? "",
            cancellations: Number(e.cancellations ?? 0),
          }));

          const downgradesSrc = (raw as { downgrades?: unknown })
            ?.downgrades as
            | { items?: ChurnDowngradeItem[] }
            | ChurnDowngradeItem[]
            | undefined;
          const downgradesArr: ChurnDowngradeItem[] = Array.isArray(
            (downgradesSrc as { items?: ChurnDowngradeItem[] })?.items,
          )
            ? (downgradesSrc as { items: ChurnDowngradeItem[] }).items
            : Array.isArray(downgradesSrc)
              ? (downgradesSrc as ChurnDowngradeItem[])
              : [];
          const downgrades = downgradesArr.map((d: ChurnDowngradeItem) => ({
            subscription_type: String(d.subscription_type ?? ""),
            period: d.period ?? "",
            period_range: d.period_range ?? undefined,
            downgrades: Number(d.downgrades ?? 0),
          }));

          const upgradesSrc = (raw as { upgrades?: unknown })?.upgrades as
            | { items?: ChurnUpgradeItem[] }
            | ChurnUpgradeItem[]
            | undefined;
          const upgradesArr: ChurnUpgradeItem[] = Array.isArray(
            (upgradesSrc as { items?: ChurnUpgradeItem[] })?.items,
          )
            ? (upgradesSrc as { items: ChurnUpgradeItem[] }).items
            : Array.isArray(upgradesSrc)
              ? (upgradesSrc as ChurnUpgradeItem[])
              : [];
          const upgrades = upgradesArr.map((u: ChurnUpgradeItem) => ({
            subscription_type: String(u.subscription_type ?? ""),
            period: u.period ?? "",
            period_range: u.period_range ?? undefined,
            upgrades: Number(u.upgrades ?? 0),
          }));

          return {
            manualCancels,
            expired,
            downgrades,
            upgrades,
            totalItems,
            currentPage,
            pageLimit,
            totalPages,
          } as ChurnReportsNormalized;
        },
        providesTags: ["ChurnReports"],
        keepUnusedDataFor: 0,
      },
    ),

    getChurnReportsCsv: builder.query<Blob, ChurnFilters & { isCsv?: boolean }>(
      {
        query: (params) => ({
          url: "admin/reports/subscriptions/churn",
          method: "GET",
          params: { ...(params || {}), isCsv: true },
          responseType: "blob",
        }),
        transformResponse: (response: unknown): Blob => response as Blob,
        keepUnusedDataFor: 0,
      },
    ),
  }),
});

export const { useGetChurnReportsQuery, useLazyGetChurnReportsCsvQuery } =
  ChurnReportsService;
